/**
 * El Markdown acotado del texto de un `Parrafo` (Iván, 2026-10-02): títulos, listas y citas
 * destacadas, para que una respuesta larga no sea un muro de texto con «▪» dentro.
 *
 * **Uno solo para todos.** La web lo usa para pintar, el validador para rechazar lo que no vale y
 * el juez para recorrer las frases. Si cada uno leyera el Markdown a su manera, el juez comprobaría
 * un texto distinto del que ve la lectora.
 *
 * Lo que entiende, y nada más (lo demás se queda como texto literal; nunca hay HTML):
 *
 * - `## Título` y `### Subtítulo`, en su propia línea. Sin cifras ni marcas: son etiquetas.
 * - Viñetas: líneas seguidas que empiezan por `- ` (también `* `, `• ` o `▪ `, que el modelo escribía
 *   antes). Numerada: líneas seguidas que empiezan por `1. ` o `1) `. Un solo nivel.
 * - Cita destacada: líneas seguidas que empiezan por `>`, terminadas en **una** marca `[n]`: de esa
 *   marca sale el estudio y la página. Sin marca, o con más de una, `marca` es `null`.
 * - Párrafos separados por una línea en blanco; un salto simple dentro de un párrafo es un espacio.
 *
 * La negrita, la cursiva y las marcas `[n]` dentro de cada parte siguen siendo cosa de quien pinta
 * la línea, como hasta ahora.
 */
const TITULO = /^(#{2,3})[ \t]+(.+?)[ \t]*#*[ \t]*$/u;
const VINETA = /^[-*•▪][ \t]+(.+)$/u;
const NUMERO = /^\d{1,2}[.)][ \t]+(.+)$/u;
const CITA = /^>[ \t]?(.*)$/u;
const MARCA_FINAL = /[ \t]*\[(\d+)\][ \t]*[.]?$/u;
export function partesDelTexto(texto) {
    const partes = [];
    let parrafo = [];
    let lista = null;
    let cita = [];
    const cerrar = () => {
        if (parrafo.length > 0)
            partes.push({ tipo: 'parrafo', texto: parrafo.join(' ') });
        if (lista !== null)
            partes.push({ tipo: 'lista', ordenada: lista.ordenada, items: lista.items });
        if (cita.length > 0)
            partes.push(citaDe(cita.join(' ')));
        parrafo = [];
        lista = null;
        cita = [];
    };
    for (const crudo of texto.replace(/\r\n?/gu, '\n').split('\n')) {
        const linea = crudo.trim();
        if (linea === '') {
            cerrar();
            continue;
        }
        const titulo = TITULO.exec(linea);
        if (titulo !== null) {
            cerrar();
            partes.push({ tipo: 'titulo', nivel: titulo[1].length === 2 ? 2 : 3, texto: titulo[2] });
            continue;
        }
        const vineta = VINETA.exec(linea);
        const numero = vineta === null ? NUMERO.exec(linea) : null;
        const item = vineta ?? numero;
        if (item !== null) {
            const ordenada = numero !== null;
            if (lista === null || lista.ordenada !== ordenada) {
                cerrar();
                lista = { ordenada, items: [] };
            }
            lista.items.push(item[1].trim());
            continue;
        }
        const lineaDeCita = CITA.exec(linea);
        if (lineaDeCita !== null) {
            if (cita.length === 0)
                cerrar();
            cita.push(lineaDeCita[1].trim());
            continue;
        }
        // Una línea sangrada justo debajo de un ítem es la continuación de ese ítem.
        if (lista !== null && /^[ \t]/u.test(crudo)) {
            const items = lista.items;
            items[items.length - 1] = `${items[items.length - 1]} ${linea}`;
            continue;
        }
        if (lista !== null || cita.length > 0)
            cerrar();
        parrafo.push(linea);
    }
    cerrar();
    return partes;
}
function citaDe(junta) {
    const marcas = junta.match(/\[\d+\]/gu) ?? [];
    const final = MARCA_FINAL.exec(junta);
    if (marcas.length !== 1 || final === null)
        return { tipo: 'cita', texto: junta, marca: null };
    return { tipo: 'cita', texto: junta.slice(0, final.index).trim(), marca: Number(final[1]) };
}
/**
 * El texto de cada parte en el que puede haber cifras y marcas: párrafos, ítems y citas. Los
 * títulos no, porque no pueden llevarlas. Es lo que recorre el juez.
 */
export function textosConCifras(texto) {
    return partesDelTexto(texto).flatMap(p => p.tipo === 'parrafo'
        ? [p.texto]
        : p.tipo === 'lista'
            ? [...p.items]
            : p.tipo === 'cita'
                ? [p.marca === null ? p.texto : `${p.texto} [${String(p.marca)}]`]
                : []);
}
//# sourceMappingURL=markdownAcotado.js.map